import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, PriceBlock } from '@/components/ui';
import { Loader2 } from '@/components/ui/icons';
import { catalogService } from '@/services';
import { useAuthStore } from '@/stores';
import type { TcgListing } from '@/types';
import { formatPrice } from '@/utils/format';
import { CatalogImage } from '../components/CatalogImage';

const REFETCH_INTERVAL_MS = 30 * 1000;

export function SellerProductsPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);

  const { data: listings, isLoading, error } = useQuery({
    queryKey: ['seller-listings', user?.id],
    queryFn: () => catalogService.getSellerListings(user!.id),
    enabled: !!user?.id,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  if (!user) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <p className="font-sans text-brand-700">{t('sellerProducts.loginRequired')}</p>
      </div>
    );
  }

  if (user.role !== 'SELLER' && user.role !== 'ADMINISTRATOR') {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <p className="font-sans text-brand-700">{t('sellerProducts.sellersOnly')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center px-4">
        <div className="inline-flex items-center gap-2 font-sans text-lg text-brand-700">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          {t('sellerProducts.loading')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <p className="font-sans text-red-600">
          {error instanceof Error ? error.message : t('sellerProducts.loadFailed')}
        </p>
      </div>
    );
  }

  if (!listings || listings.length === 0) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <h1 className="mb-4 font-heading text-heading-xl text-brand-950">
          {t('sellerProducts.title')}
        </h1>
        <p className="font-sans text-brand-700">{t('sellerProducts.empty')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-2 font-heading text-heading-xl text-brand-950">
        {t('sellerProducts.title')}
      </h1>
      <p className="mb-6 max-w-2xl font-sans text-sm text-brand-700">
        {t('sellerProducts.subtitle')}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {listings.map((listing: TcgListing) => {
          const product = listing.product;
          const name = product?.name ?? listing.masterCard?.name ?? '—';
          const price = product?.price ?? 0;
          const stock = product?.stock ?? 0;
          const imageUrl =
            product?.images && product.images.length > 0
              ? product.images[0].url
              : listing.masterCard?.imageUrl ?? 'https://via.placeholder.com/240x336?text=TCG';
          const detailUrl =
            listing.masterCard?.id && listing.setCode
              ? `/tcg/cards/${listing.masterCard.id}?setCode=${encodeURIComponent(
                  listing.setCode,
                )}&setRarityCode=${encodeURIComponent(
                  listing.setRarityCode || '',
                )}&setEdition=${encodeURIComponent(listing.setEdition || '')}`
              : product?.id
                ? `/products/${product.id}`
                : null;
          const isInStock = stock > 0;
          const statusLabel =
            listing.status === 'ACTIVE'
              ? t('sellerProducts.statusActive')
              : listing.status === 'ARCHIVED'
                ? t('sellerProducts.statusArchived')
                : t('sellerProducts.statusDraft');

          return (
            <article
              key={listing.id}
              onClick={() => {
                if (detailUrl) navigate(detailUrl);
              }}
              onKeyDown={(event) => {
                if (!detailUrl) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(detailUrl);
                }
              }}
              role={detailUrl ? 'button' : undefined}
              tabIndex={detailUrl ? 0 : undefined}
              className="group grid cursor-pointer grid-cols-[104px_minmax(0,1fr)] overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <div className="aspect-[2/3] overflow-hidden bg-brand-100">
                <CatalogImage
                  src={imageUrl}
                  alt={name}
                  className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2 p-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={isInStock ? 'success' : 'neutral'}>
                    {isInStock
                      ? t('sellerProducts.stockCount', { count: stock })
                      : t('common.outOfStock')}
                  </Badge>
                  <Badge variant={listing.status === 'ACTIVE' ? 'brand' : 'neutral'}>
                    {statusLabel}
                  </Badge>
                </div>
                <div className="min-w-0">
                  <h2 className="line-clamp-2 font-heading text-heading-sm text-brand-950">
                    {name}
                  </h2>
                  <p className="mt-1 line-clamp-2 font-sans text-xs text-brand-700">
                    {[listing.setCode, listing.condition, listing.printingVariant]
                      .filter(Boolean)
                      .join(' / ') || t('sellerProducts.productListing')}
                  </p>
                </div>
                <PriceBlock
                  className="mt-auto"
                  amount={
                    typeof price === 'number' && !Number.isNaN(price)
                      ? formatPrice(price, product?.currency ?? 'COP')
                      : '—'
                  }
                  secondary={listing.sellerLabel ?? t('sellerProducts.listing')}
                  size="sm"
                />
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
